import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Search, X, FlaskConical, ArrowRight, Trash2, CalendarDays,
} from "lucide-react";
import MOShell, {
  useMOAccess, isWritable, LoadingBlock, NoAccessScreen,
} from "@/components/masterOrder/MOShell";
import { Field, Input } from "@/pages/masterOrder/MOClients";
import CurrencyField from "@/components/masterOrder/CurrencyField";
import {
  listIngredients, createIngredient, updateIngredient,
  listIngredientPricesByIng, createIngredientPrice,
  INGREDIENT_BASE_UNITS, PURCHASE_UNITS_BY_FAMILY, unitFamily,
  mapCostingError, unitLabel,
} from "@/lib/masterOrderApi";
import { formatApiError } from "@/lib/api";

const showErr = (e) => toast.error(mapCostingError(e) || formatApiError(e));
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("ru-RU") : "—");
const todayISO = () => new Date().toISOString().slice(0, 10);

/** Latest price per currency for an ingredient — sorted history desc by effective_at. */
function latestPricesByCurrency(prices) {
  const byCur = new Map();
  for (const p of prices) {
    const cur = p.currency_code;
    const eff = p.effective_at || "";
    if (!byCur.has(cur) || eff > byCur.get(cur).effective_at) byCur.set(cur, p);
  }
  return Array.from(byCur.values()).sort((a, b) => a.currency_code.localeCompare(b.currency_code));
}

/* ============================== Create/Edit ============================= */
function IngredientDrawer({ open, mode, initial, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", base_unit: "g", category: "", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && initial) {
      setForm({
        name: initial.name || "",
        base_unit: initial.base_unit || "g",
        category: initial.category || "",
        notes: initial.notes || "",
      });
    } else {
      setForm({ name: "", base_unit: "g", category: "", notes: "" });
    }
  }, [open, mode, initial]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Введите название"); return; }
    setSaving(true);
    try {
      if (mode === "edit" && initial) {
        const patch = {
          name: form.name.trim(),
          category: form.category.trim() || null,
          notes: form.notes.trim() || null,
        };
        const upd = await updateIngredient(initial.id, patch);
        toast.success("Ингредиент сохранён");
        onSaved(upd);
      } else {
        const payload = {
          name: form.name.trim(),
          base_unit: form.base_unit,
          category: form.category.trim() || null,
          notes: form.notes.trim() || null,
          is_active: true,
        };
        const created = await createIngredient(payload);
        toast.success("Ингредиент создан");
        onSaved(created);
      }
      onClose();
    } catch (e) { showErr(e); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(35,23,16,0.42)" }} onClick={onClose}>
      <div
        className="h-full w-full sm:w-[440px] bg-white p-5 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: "-30px 0 60px -20px rgba(35,23,16,0.4)" }}
        data-testid="mo-ingredient-drawer"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="text-[20px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
            {mode === "edit" ? "Редактировать ингредиент" : "Новый ингредиент"}
          </div>
          <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full hover:bg-black/5" aria-label="Закрыть">
            <X size={16} color="#8B6F4B" />
          </button>
        </div>

        <div className="space-y-3">
          <Field label="Название *">
            <Input value={form.name} onChange={(v) => set("name", v)} testid="mo-ingredient-name" placeholder="Мука пшеничная T55" />
          </Field>
          <Field label="Базовая единица *">
            <select
              value={form.base_unit}
              onChange={(e) => set("base_unit", e.target.value)}
              disabled={mode === "edit"}
              className="w-full h-10 px-3 rounded-lg text-[13px] outline-none bg-white disabled:opacity-60"
              style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
              data-testid="mo-ingredient-base-unit"
            >
              {INGREDIENT_BASE_UNITS.map((u) => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </select>
            {mode === "edit" && (
              <p className="text-[11px] mt-1" style={{ color: "#8B6F4B" }}>
                Базовую единицу нельзя менять — она заморожена в исторических ценах и калькуляциях.
              </p>
            )}
          </Field>
          <Field label="Категория">
            <Input value={form.category} onChange={(v) => set("category", v)} testid="mo-ingredient-category" placeholder="Мука · Сливки · Шоколад…" />
          </Field>
          <Field label="Заметки">
            <textarea
              value={form.notes} onChange={(e) => set("notes", e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-lg text-[13px] outline-none resize-none"
              style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
              data-testid="mo-ingredient-notes"
            />
          </Field>
        </div>

        <div className="flex justify-end gap-2 mt-6 sticky bottom-0 bg-white pt-3">
          <button onClick={onClose} className="h-10 px-4 rounded-xl text-[12.5px]"
                  style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}>
            Отменить
          </button>
          <button onClick={submit} disabled={saving}
                  className="h-10 px-5 rounded-xl text-[12.5px] disabled:opacity-60"
                  style={{ background: "#4A3728", color: "#F7EFE0" }}
                  data-testid="mo-ingredient-save">
            {saving ? "Сохраняем..." : "Сохранить"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================== Add Price =============================== */
function AddPriceDrawer({ open, ingredient, onClose, onCreated }) {
  const [form, setForm] = useState({
    price: "", currency_code: "EUR", purchase_quantity: "1",
    purchase_unit: "", effective_at: todayISO(), supplier_name: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const family = ingredient ? unitFamily(ingredient.base_unit) : "mass";
  const purchaseUnitOptions = PURCHASE_UNITS_BY_FAMILY[family] || [];

  useEffect(() => {
    if (!open || !ingredient) return;
    setForm({
      price: "", currency_code: "EUR", purchase_quantity: "1",
      purchase_unit: purchaseUnitOptions[0]?.value || "",
      effective_at: todayISO(), supplier_name: "", notes: "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ingredient]);

  if (!open || !ingredient) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    // Accept both "1.69" and "1,69" — normalize to dot before validating/sending.
    const normDec = (v) => (typeof v === "string" ? v.replace(",", ".").trim() : v);
    const priceNorm = normDec(form.price);
    const qtyNorm = normDec(form.purchase_quantity);
    if (!priceNorm || Number(priceNorm) <= 0) { toast.error("Укажите цену больше 0"); return; }
    if (!qtyNorm || Number(qtyNorm) <= 0) { toast.error("Количество должно быть больше 0"); return; }
    if (!form.purchase_unit) { toast.error("Выберите единицу закупки"); return; }
    if (!form.currency_code || form.currency_code.length !== 3) { toast.error("Валюта: код из 3 букв (EUR, PLN, RUB…)"); return; }
    if (!form.effective_at) { toast.error("Укажите дату действия цены"); return; }
    setSaving(true);
    try {
      const payload = {
        ingredient_id: ingredient.id,
        price: priceNorm,
        currency_code: form.currency_code.toUpperCase(),
        purchase_quantity: qtyNorm,
        purchase_unit: form.purchase_unit,
        effective_at: new Date(form.effective_at + "T00:00:00Z").toISOString(),
        supplier_name: form.supplier_name.trim() || null,
        notes: form.notes.trim() || null,
      };
      const created = await createIngredientPrice(payload);
      toast.success("Цена добавлена");
      onCreated(created);
      onClose();
    } catch (e) { showErr(e); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(35,23,16,0.42)" }} onClick={onClose}>
      <div
        className="h-full w-full sm:w-[440px] bg-white p-5 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: "-30px 0 60px -20px rgba(35,23,16,0.4)" }}
        data-testid="mo-price-drawer"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="text-[20px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
            Новая цена
          </div>
          <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full hover:bg-black/5" aria-label="Закрыть">
            <X size={16} color="#8B6F4B" />
          </button>
        </div>
        <div className="text-[12px] mb-4" style={{ color: "#8B6F4B" }}>
          {ingredient.name} · базовая единица <b>{unitLabel(ingredient.base_unit)}</b>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-[1fr_110px] gap-2">
            <Field label="Цена *">
              <Input value={form.price} onChange={(v) => set("price", v)} testid="mo-price-amount" placeholder="1.85" />
            </Field>
            <Field label="Валюта *">
              <CurrencyField value={form.currency_code} onChange={(v) => set("currency_code", v)} testid="mo-price-currency" />
            </Field>
          </div>
          <div className="grid grid-cols-[1fr_110px] gap-2">
            <Field label="Количество в упаковке *">
              <Input value={form.purchase_quantity} onChange={(v) => set("purchase_quantity", v)} testid="mo-price-qty" placeholder="1" />
            </Field>
            <Field label="Единица упаковки *">
              <select value={form.purchase_unit} onChange={(e) => set("purchase_unit", e.target.value)}
                      className="w-full h-10 px-3 rounded-lg text-[13px] outline-none bg-white"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      data-testid="mo-price-unit">
                {purchaseUnitOptions.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </Field>
          </div>
          <p className="text-[11px] -mt-1" style={{ color: "#8B6F4B" }}>
            {ingredient.base_unit === "ml"
              ? "Можно указывать цену за миллилитры или литры."
              : ingredient.base_unit === "g"
                ? "Можно указывать цену за граммы или килограммы."
                : "Можно указывать цену за штуку."}
          </p>
          <Field label="Дата действия *">
            <Input value={form.effective_at} onChange={(v) => set("effective_at", v)} testid="mo-price-effective" type="date" />
          </Field>
          <Field label="Поставщик">
            <Input value={form.supplier_name} onChange={(v) => set("supplier_name", v)} testid="mo-price-supplier" placeholder="Не обязательно" />
          </Field>
          <Field label="Заметки">
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2}
                      className="w-full px-3 py-2 rounded-lg text-[13px] outline-none resize-none"
                      style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      data-testid="mo-price-notes" />
          </Field>
        </div>

        <div className="flex justify-end gap-2 mt-6 sticky bottom-0 bg-white pt-3">
          <button onClick={onClose} className="h-10 px-4 rounded-xl text-[12.5px]"
                  style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}>
            Отменить
          </button>
          <button onClick={submit} disabled={saving}
                  className="h-10 px-5 rounded-xl text-[12.5px] disabled:opacity-60"
                  style={{ background: "#4A3728", color: "#F7EFE0" }}
                  data-testid="mo-price-save">
            {saving ? "Сохраняем..." : "Сохранить цену"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteIngredientDialog({ ingredient, busy, onCancel, onConfirm }) {
  if (!ingredient) return null;
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center p-4" style={{ background: "rgba(35,23,16,.5)" }}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6" data-testid="mo-ingredient-delete-dialog">
        <h3 className="text-[23px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Удалить ингредиент?</h3>
        <p className="mt-2 text-[13px]" style={{ color: "#5B4326" }}>Ингредиент «{ingredient.name}» будет удалён из рабочего справочника. Сохранённые ранее расчёты и история цен останутся без изменений.</p>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="h-10 px-4 rounded-xl text-[12.5px]" style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}>Отмена</button>
          <button type="button" onClick={onConfirm} disabled={busy} className="h-10 px-5 rounded-xl text-[12.5px] disabled:opacity-60" style={{ background: "#8D3C32", color: "#FFF9F0" }} data-testid="mo-ingredient-delete-confirm">Удалить</button>
        </div>
      </div>
    </div>
  );
}

/* ============================ Detail Drawer ============================= */
function IngredientDetail({ open, ingredient, canWrite, onClose, onEdit, onIngredientRemoved }) {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceOpen, setPriceOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);

  const reload = async () => {
    if (!ingredient) return;
    setLoading(true);
    try {
      const list = await listIngredientPricesByIng(ingredient.id);
      list.sort((a, b) => (b.effective_at || "").localeCompare(a.effective_at || ""));
      setPrices(list);
    } catch (e) { showErr(e); } finally { setLoading(false); }
  };
  useEffect(() => { if (open && ingredient) reload(); /* eslint-disable-line */ }, [open, ingredient?.id]);

  if (!open || !ingredient) return null;

  const latest = latestPricesByCurrency(prices);

  const removeIngredient = async () => {
    setRemoving(true);
    try {
      await updateIngredient(ingredient.id, { is_active: false });
      toast.success("Ингредиент удалён из справочника.");
      setConfirmingRemoval(false);
      onIngredientRemoved(ingredient.id);
    } catch (e) { showErr(e); } finally { setRemoving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end" style={{ background: "rgba(35,23,16,0.42)" }} onClick={onClose}>
      <div
        className="h-full w-full sm:w-[560px] bg-white overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: "-30px 0 60px -20px rgba(35,23,16,0.4)" }}
        data-testid="mo-ingredient-detail"
      >
        <div className="p-5 border-b" style={{ borderColor: "#EFE4D0" }}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10.5px] tracking-[0.24em] uppercase" style={{ color: "#8B6F4B" }}>Ингредиент</div>
              <div className="text-[24px] mt-0.5" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}
                   data-testid="mo-detail-name">{ingredient.name}</div>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-[11.5px] px-2 py-0.5 rounded-lg" style={{ background: "#F5EBD8", color: "#5B4326" }}>
                  Базовая единица · {unitLabel(ingredient.base_unit)}
                </span>
                {ingredient.category && (
                  <span className="text-[11px] px-2 py-0.5 rounded-lg" style={{ background: "#EEE5D2", color: "#5B4326" }}>
                    {ingredient.category}
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full hover:bg-black/5" aria-label="Закрыть">
              <X size={16} color="#8B6F4B" />
            </button>
          </div>

          {canWrite && (
            <div className="flex flex-wrap gap-2 mt-4">
              <button onClick={onEdit} className="h-9 px-4 rounded-xl text-[12px]"
                      style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}
                      data-testid="mo-detail-edit">
                Редактировать
              </button>
              <button onClick={() => setPriceOpen(true)} className="h-9 px-4 rounded-xl text-[12px] inline-flex items-center gap-1.5"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-detail-add-price">
                <Plus size={12} /> Добавить цену
              </button>
              <button onClick={() => setConfirmingRemoval(true)} disabled={removing}
                      className="h-9 px-4 rounded-xl text-[12px] inline-flex items-center gap-1.5 disabled:opacity-60"
                      style={{ background: "#FFFFFF", color: "#8D3C32", boxShadow: "inset 0 0 0 1px #D9C6A6" }}
                      data-testid="mo-detail-delete">
                <Trash2 size={12} /> Удалить ингредиент
              </button>
            </div>
          )}
        </div>

        {/* Latest prices per currency */}
        <div className="p-5">
          <div className="text-[10.5px] tracking-[0.24em] uppercase mb-2" style={{ color: "#8B6F4B" }}>Актуальные цены</div>
          {latest.length === 0 ? (
            <div className="rounded-lg p-4 text-[12.5px]"
                 style={{ background: "#FBF5EA", color: "#8B6F4B", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                 data-testid="mo-detail-latest-empty">
              Цен ещё нет. {canWrite && "Добавьте первую цену, чтобы этот ингредиент можно было использовать в калькуляциях."}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="mo-detail-latest">
              {latest.map((p) => (
                <div key={p.id} className="rounded-lg p-3"
                     style={{ background: "linear-gradient(180deg,#FBF5EA,#F5EBD8)", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                     data-testid={`mo-detail-latest-${p.currency_code}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] tracking-[0.16em] uppercase" style={{ color: "#8B6F4B" }}>{p.currency_code}</span>
                    <span className="text-[10px]" style={{ color: "#8B6F4B" }}>с {fmtDate(p.effective_at)}</span>
                  </div>
                  <div className="mt-1 text-[17px] tabular-nums" style={{ color: "#2A1D14", fontFamily: "'Cormorant Garamond', serif" }}>
                    {p.price} <span className="text-[13px]" style={{ color: "#5B4326" }}>/ {p.purchase_quantity} {unitLabel(p.purchase_unit)}</span>
                  </div>
                  <div className="text-[11px] mt-0.5 tabular-nums" style={{ color: "#8B6F4B" }}>
                    ≈ {p.unit_cost} {p.currency_code} / {unitLabel(p.normalized_unit)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Full history */}
          <div className="text-[10.5px] tracking-[0.24em] uppercase mt-6 mb-2" style={{ color: "#8B6F4B" }}>История цен</div>
          {loading ? (
            <div className="text-[12.5px]" style={{ color: "#8B6F4B" }}>Загрузка…</div>
          ) : prices.length === 0 ? (
            <div className="text-[12.5px]" style={{ color: "#8B6F4B" }} data-testid="mo-detail-history-empty">
              История пуста.
            </div>
          ) : (
            <div className="rounded-lg overflow-hidden" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-detail-history">
              <table className="w-full text-[12px]">
                <thead style={{ background: "#F5EBD8", color: "#5B4326" }}>
                  <tr>
                    <th className="text-left px-3 py-1.5 font-normal">Дата</th>
                    <th className="text-left px-3 py-1.5 font-normal">Цена</th>
                    <th className="text-left px-3 py-1.5 font-normal">Упаковка</th>
                    <th className="text-left px-3 py-1.5 font-normal">Ед. цена</th>
                    <th className="text-left px-3 py-1.5 font-normal">Поставщик</th>
                  </tr>
                </thead>
                <tbody>
                  {prices.map((p) => (
                    <tr key={p.id} className="border-t" style={{ borderColor: "#EFE4D0" }}
                        data-testid={`mo-detail-history-row-${p.id}`}>
                      <td className="px-3 py-1.5 tabular-nums" style={{ color: "#4A3728" }}>{fmtDate(p.effective_at)}</td>
                      <td className="px-3 py-1.5 tabular-nums" style={{ color: "#2A1D14" }}>{p.price} {p.currency_code}</td>
                      <td className="px-3 py-1.5 tabular-nums" style={{ color: "#4A3728" }}>{p.purchase_quantity} {unitLabel(p.purchase_unit)}</td>
                      <td className="px-3 py-1.5 tabular-nums" style={{ color: "#4A3728" }}>{p.unit_cost} / {unitLabel(p.normalized_unit)}</td>
                      <td className="px-3 py-1.5" style={{ color: "#8B6F4B" }}>{p.supplier_name || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <AddPriceDrawer
          open={priceOpen} ingredient={ingredient}
          onClose={() => setPriceOpen(false)}
          onCreated={(p) => setPrices((old) => [p, ...old])}
        />
        <DeleteIngredientDialog ingredient={confirmingRemoval ? ingredient : null} busy={removing} onCancel={() => setConfirmingRemoval(false)} onConfirm={removeIngredient} />
      </div>
    </div>
  );
}

/* =============================== Page =================================== */
export default function MOIngredients() {
  const { mode, loading: aLoad } = useMOAccess();
  const canWrite = isWritable(mode);
  const [urlParams] = useSearchParams();
  const searchFromUrl = urlParams.get("search") || "";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchFromUrl);
  useEffect(() => { setQ(searchFromUrl); }, [searchFromUrl]);
  const [familyFilter, setFamilyFilter] = useState("all"); // all | mass | volume | count
  const [pricesByIng, setPricesByIng] = useState({}); // id -> [prices]
  const [drawer, setDrawer] = useState({ open: false, mode: "create", ingredient: null });
  const [detail, setDetail] = useState(null); // ingredient object

  const load = async () => {
    setLoading(true);
    try {
      const list = await listIngredients();
      setItems(list);
      // fetch prices in parallel — small directory, fine for MVP
      const priceLists = await Promise.all(list.map((i) => listIngredientPricesByIng(i.id).catch(() => [])));
      const map = {};
      list.forEach((i, k) => { map[i.id] = priceLists[k] || []; });
      setPricesByIng(map);
    } catch (e) { showErr(e); } finally { setLoading(false); }
  };
  useEffect(() => {
    if (aLoad) return;
    if (mode === "no_access" || mode === "demo") { setLoading(false); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aLoad, mode]);

  const upsertItem = (updated) => {
    setItems((old) => {
      const idx = old.findIndex((x) => x.id === updated.id);
      if (idx < 0) return [updated, ...old];
      const copy = old.slice(); copy[idx] = updated; return copy;
    });
    setDetail((d) => (d && d.id === updated.id ? updated : d));
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items
      .filter((it) => {
        if (it.is_active === false) return false;
        if (familyFilter !== "all" && unitFamily(it.base_unit) !== familyFilter) return false;
        if (!s) return true;
        return (it.name || "").toLowerCase().includes(s)
            || (it.category || "").toLowerCase().includes(s);
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "ru"));
  }, [items, q, familyFilter]);

  const openDetail = async (it) => {
    // Refresh prices for that ingredient when opened
    setDetail(it);
    try {
      const list = await listIngredientPricesByIng(it.id);
      setPricesByIng((old) => ({ ...old, [it.id]: list }));
    } catch (_) { /* ignore */ }
  };

  const renderContent = () => {
    if (aLoad || loading) return <LoadingBlock />;
    if (mode === "no_access" || mode === "demo") return <NoAccessScreen />;

    const hasAnything = items.some((item) => item.is_active !== false);
    return (
      <>
        {/* Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" color="#8B6F4B" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Поиск по названию или категории..."
              className="w-full h-10 pl-9 pr-4 rounded-xl text-[13px] outline-none bg-white"
              style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
              data-testid="mo-ingredients-search"
            />
          </div>
          <select value={familyFilter} onChange={(e) => setFamilyFilter(e.target.value)}
                  className="h-10 px-3 rounded-xl text-[13px] outline-none bg-white"
                  style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                  data-testid="mo-ingredients-family-filter">
            <option value="all">Все единицы</option>
            <option value="mass">Масса (g)</option>
            <option value="volume">Объём (ml)</option>
            <option value="count">Штуки</option>
          </select>
          {canWrite && (
            <button onClick={() => setDrawer({ open: true, mode: "create", ingredient: null })}
                    className="h-10 px-5 rounded-xl text-[13px] inline-flex items-center gap-2"
                    style={{ background: "#4A3728", color: "#F7EFE0" }}
                    data-testid="mo-ingredients-new">
              <Plus size={15} strokeWidth={1.8} /> Добавить ингредиент
            </button>
          )}
        </div>

        {/* Empty state */}
        {!hasAnything ? (
          <div className="rounded-xl bg-white p-10 text-center"
               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-ingredients-empty">
            <FlaskConical className="mx-auto" size={22} strokeWidth={1.5} color="#8B6F4B" />
            <div className="mt-3 text-[20px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
              Ингредиентов пока нет
            </div>
            <p className="text-[12.5px] mt-1.5 max-w-md mx-auto" style={{ color: "#8B6F4B" }}>
              Добавьте первый ингредиент, чтобы использовать его в рецептурах и калькуляциях.
            </p>
            {canWrite && (
              <button onClick={() => setDrawer({ open: true, mode: "create", ingredient: null })}
                      className="mt-4 h-10 px-5 rounded-xl text-[13px] inline-flex items-center gap-2"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-ingredients-empty-cta">
                <Plus size={14} /> Добавить ингредиент
              </button>
            )}
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl bg-white p-8 text-center text-[13px]"
               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#8B6F4B" }} data-testid="mo-ingredients-nomatch">
            Ничего не найдено. Попробуйте изменить поиск или фильтр.
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block rounded-xl overflow-hidden bg-white"
                 style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
              <table className="w-full text-[12.5px]" data-testid="mo-ingredients-table">
                <thead style={{ background: "#F5EBD8", color: "#5B4326" }}>
                  <tr>
                    <th className="text-left px-3 py-2 font-normal">Ингредиент</th>
                    <th className="text-left px-3 py-2 font-normal">Базовая единица</th>
                    <th className="text-left px-3 py-2 font-normal">Последняя цена</th>
                    <th className="text-left px-3 py-2 font-normal">Дата</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it) => {
                    const latest = latestPricesByCurrency(pricesByIng[it.id] || []);
                    return (
                      <tr key={it.id}
                          className="border-t hover:bg-[#FBF5EA] cursor-pointer"
                          style={{ borderColor: "#EFE4D0" }}
                          onClick={() => openDetail(it)}
                          data-testid={`mo-ingredient-row-${it.id}`}>
                        <td className="px-3 py-2" style={{ color: "#2A1D14" }}>
                          <div>{it.name}</div>
                          {it.category && (
                            <div className="text-[10.5px] mt-0.5" style={{ color: "#8B6F4B" }}>{it.category}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 tabular-nums" style={{ color: "#4A3728" }}>{unitLabel(it.base_unit)}</td>
                        <td className="px-3 py-2 tabular-nums" style={{ color: "#2A1D14" }}>
                          {latest.length === 0 ? (
                            <span style={{ color: "#8B6F4B" }}>—</span>
                          ) : (
                            <div className="flex flex-col gap-0.5">
                              {latest.map((p) => (
                                <span key={p.id}>
                                  {p.price} {p.currency_code} <span style={{ color: "#8B6F4B" }}>/ {p.purchase_quantity} {unitLabel(p.purchase_unit)}</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 tabular-nums" style={{ color: "#8B6F4B" }}>
                          {latest.length === 0 ? "—" : fmtDate(latest[0].effective_at)}
                        </td>
                        <td className="px-3 py-2 text-right"><ArrowRight size={13} color="#8B6F4B" /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-2" data-testid="mo-ingredients-cards">
              {filtered.map((it) => {
                const latest = latestPricesByCurrency(pricesByIng[it.id] || []);
                return (
                  <button key={it.id}
                          onClick={() => openDetail(it)}
                          className="w-full text-left rounded-xl bg-white p-4 hover:bg-[#FBF5EA] transition"
                          style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                          data-testid={`mo-ingredient-card-${it.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-[14px] truncate" style={{ color: "#2A1D14" }}>{it.name}</div>
                        <div className="text-[11px] mt-0.5 flex items-center gap-2 flex-wrap" style={{ color: "#8B6F4B" }}>
                          <span>Базовая · {unitLabel(it.base_unit)}</span>
                          {it.category && <span>· {it.category}</span>}
                        </div>
                      </div>
                      <ArrowRight size={13} color="#8B6F4B" />
                    </div>
                    <div className="mt-3">
                      {latest.length === 0 ? (
                        <div className="text-[11.5px]" style={{ color: "#8B6F4B" }}>Цен ещё нет</div>
                      ) : (
                        <div className="flex flex-col gap-0.5 text-[12px] tabular-nums" style={{ color: "#2A1D14" }}>
                          {latest.map((p) => (
                            <div key={p.id} className="flex items-center gap-1.5">
                              <span className="text-[10px] px-1.5 py-0.5 rounded-lg" style={{ background: "#F5EBD8", color: "#5B4326" }}>{p.currency_code}</span>
                              <span>{p.price} / {p.purchase_quantity} {unitLabel(p.purchase_unit)}</span>
                              <span className="text-[10.5px] inline-flex items-center gap-0.5" style={{ color: "#8B6F4B" }}>
                                <CalendarDays size={10} /> {fmtDate(p.effective_at)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}

        <IngredientDrawer
          open={drawer.open} mode={drawer.mode} initial={drawer.ingredient}
          onClose={() => setDrawer({ open: false, mode: "create", ingredient: null })}
          onSaved={(ing) => { upsertItem(ing); load(); }}
        />
        <IngredientDetail
          open={!!detail} ingredient={detail} canWrite={canWrite}
          onClose={() => setDetail(null)}
          onEdit={() => setDrawer({ open: true, mode: "edit", ingredient: detail })}
          onIngredientRemoved={(ingredientId) => { setItems((current) => current.map((item) => item.id === ingredientId ? { ...item, is_active: false } : item)); setDetail(null); }}
        />
      </>
    );
  };

  return (
    <MOShell active="ingredients" title="Ингредиенты"
             subtitle="Ваш справочник продуктов и истории закупочных цен.">
      {renderContent()}
    </MOShell>
  );
}
